// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/**
 * Enumeration of file types.
 */
export enum FileType {
    /**
     * The file type is unknown.
     */
    Unknown = 0,
    /**
     * A regular file.
     */
    File = 1,
    /**
     * A directory.
     */
    Directory = 2,
}

/**
 * Permissions of a file.
 */
export enum FilePermission {
    /**
     * The file is readonly.
     */
    Readonly = 1,
}

/**
 * Type taken from the VSCode extension API.
 */
export interface FileStat {
    /**
     * The type of the file, e.g. is a regular file or a directory.
     */
    type: FileType;

    /**
     * The creation timestamp in milliseconds elapsed since January 1, 1970 00:00:00 UTC.
     */
    ctime: number;

    /**
     * The modification timestamp in milliseconds elapsed since January 1, 1970 00:00:00 UTC.
     *
     * *Note:* If the file changed, it is important to provide an updated `mtime` that advanced
     * from the previous value. Otherwise there may be optimizations in place that will not show
     * the updated file contents in an editor for example.
     */
    mtime: number;

    /**
     * The size in bytes.
     *
     * *Note:* If the file changed, it is important to provide an updated `size`. Otherwise there
     * may be optimizations in place that will not show the updated file contents in an editor for
     * example.
     */
    size: number;

    /**
     * The permissions of the file, e.g. whether the file is readonly.
     */
    permissions?: FilePermission;
}

/**
 * The filesystem provider defines what the VSCode editor needs to read, write, discover,
 * and to manage files and folders. It allows extensions to serve files from remote places,
 * like ftp-servers, and to seamlessly integrate those into the editor.
 *
 * * *Note 1:* The filesystem provider API works with `TLocation` and assumes hierarchical
 * paths, e.g. `/my/path` is a child of `/my/` and a parent of `foo:/my/path/deeper`.
 * * *Note 2:* The word 'file' is often used to denote all {@link FileType kinds} of files, e.g.
 * folders, symbolic links, and regular files.
 *
 * {@see https://github.com/microsoft/vscode/blob/3cc75a54445e92fa3406d2f3a0b30c4762be19bc/src/vscode-dts/vscode.d.ts#L9203}
 */
export interface IFileSystem<TLocation> {
    /**
     * Create a new directory (Note, that new files are created via `write`-calls).
     *
     * @param location The location of the new folder.
     * @throws when `location` already exists.
     */
    createDirectory: (location: TLocation) => Promise<void>;

    /**
     * Write data to a file, replacing its entire contents.
     *
     * @param location The location of the file.
     * @param content The new content of the file.
     */
    writeFile: (location: TLocation, content: Uint8Array) => Promise<void>;

    /**
     * Delete a file.
     *
     * @param location The resource that is to be deleted.
     * @param options Defines if deletion of folders is recursive.
     * @throws {@linkcode FileSystemError.FileNotFound FileNotFound} when `location` doesn't exist.
     */
    delete: (location: TLocation, options?: { readonly recursive?: boolean }) => Promise<void>;

    /**
     * Rename a file or folder.
     *
     * @param oldLocation The existing file.
     * @param newLocation The new location.
     * @throws when `oldLocation` doesn't exist.
     */
    rename: (oldLocation: TLocation, newLocation: TLocation) => Promise<void>;

    /**
     * Copy files or folders. Implementing this function is optional but it will speedup
     * the copy operation.
     *
     * @param source The existing file.
     * @param destination The destination location.
     * @param options Defines if existing files should be overwritten.
     * @throws when `source` doesn't exist.
     */
    copy: (source: TLocation, destination: TLocation) => void;

    /**
     * Retrieve metadata about a file.

     * @param location The location of the file to retrieve metadata about.
     * @returns The file metadata about the file.
     * @throws when `location` doesn't exist.
     */
    stat: (location: TLocation) => Promise<FileStat>;

    /**
     * Retrieve all entries of a {@link FileType.Directory directory}.
     *
     * @param location The location of the folder.
     * @returns An array of name/type-tuples or a thenable that resolves to such.
     * @throws when `location` doesn't exist.
     */
    readDirectory: (location: TLocation) => Promise<[string, FileType][]>;

    /**
     * Read the entire contents of a file.
     *
     * @param location The location of the file.
     * @returns An array of bytes or a thenable that resolves to such.
     * @throws when `location` doesn't exist.
     */
    readFile: (location: TLocation) => Promise<Uint8Array>;
}
